import { AppTagItemEntity } from '@datachain/ui-sdk/components/dc-types';
import { createAction, props } from '@ngrx/store';
import { OrderedSet, Map } from 'immutable';
import { AppMetadataEntity } from 'src/app/ui/components/app-metadata/app-metadata.entity';

import {
	CharacteristicType,
	DcTimestampFormat,
} from '../../core/dc-core-types';
import { DepotHeaderItemEntity } from '../components/create-entity-from-depot-modal/depot-header-item.entity';
import { DepotHeaderListEntity } from '../components/create-entity-from-depot-modal/depot-header-list.entity';
import { DepotListEntity } from '../components/depot-list/depot-list.entity';
import { DepotSchemaEntity } from '../components/depot-schema-viewer/depot-schema.entity';
import { DepotSchemaConfig } from '../domain/depot-schema-config';
import { SupportedLanguageEntity } from '../domain/supported-language.entity';

export enum DepotActions {
	InitDepotList = '[Depot] get depot list',
	ResetAvailableHeaders = '[Depot] close create depot entity modal',
	RefreshDepotList = '[Depot] refresh Depot list',
	UpdateEntitiesCountForDepot = '[Depot] get entity count for depots',
	UpdateEntitiesCountForDepotSuccess = '[BE][Depot] get entity count for depots success',
	UpdateEntitiesCountForDepotFailure = '[BE][Depot] get entity count for depots failure',
	FetchDepotListFail = '[BE][Depot] fetch Depot list fail',
	FetchDepotListSuccess = '[BE][Depot] fetch Depot list success',
	NavigateToV1ListAndCreateEntity = '[Depot] Navigate to V1 list and create Entity from target depot',
	NavigateToDepotRoute = '[Depot] navigate to Depot route',
	NavigateToDepotCreateRoute = '[Depot] navigate to Depot create route',
	NavigateToDepotDuplicateRoute = '[Depot] navigate to Depot duplicate route',
	UpdateIncludePublic = '[Depot] update include public',
	OpenDeleteConfirmationPopup = '[Depot] open delete confirm popup',
	OpenCreateDepotEntityModal = '[Depot] open V2 create entity from target depot modal',
	DeleteDepot = '[Depot] delete depot',
	BulkDeleteDepots = '[Depot] delete many depots',
	DeleteDepotFail = '[BE][Depot] delete depot fail',
	ToggleDepotStatus = '[Depot] toggle depot status',
	InitAvailableDepotHeadersForEntityCreation = '[Depot] init available depot headers for entity creation',
	FetchDepotHeadersSuccess = '[BE][Depot] fetch depot headers success',
	FetchDepotHeadersFail = '[BE][Depot] fetch depot headers fail',
	CreateEntityFromDepot = '[Depot] Create entity from depot',
	CreateEntityFromDepotSuccess = '[BE][Depot] Create entity from depot success',
	CreateEntityFromDepotFail = '[BE][Depot] Create entity from depot fail',
	OpenDepotHeaderFieldsSelection = '[Depot] open depot header fields selection',
	TriggerDetectAvailableHeaders = '[Depot] Trigger Schema Headers detection',
	ReplaceCurrentLabel = '[Depot] Replace header label search term with',
	ReconstructDepotSchemaMappingSuccess = '[Depot][Schema Mapping] Reconstruct headers schema for depot success',
	ReconstructDepotSchemaMappingError = '[Depot][Schema Mapping] Reconstruct headers schema for depot failure',
	FetchDepotSchemaMappingSuccess = '[BE][Depot][Schema Mapping] Get headers schema for depot success',
	FetchDepotSchemaMappingError = '[BE][Depot][Schema Mapping] Get headers schema for depot failure',
	InitDepotHeaderSetting = '[Depot][Schema Mapping] Init depot headers setting',
	GetDepotHeaderSettingSuccess = '[Depot] Get depot headers setting success',
	GetDepotHeaderSettingFail = '[Depot] Get depot headers setting fail',
}

export const initDepotList = createAction(DepotActions.InitDepotList);

export const refreshDepotList = createAction(DepotActions.RefreshDepotList);

export const fetchDepotListSuccess = createAction(
	DepotActions.FetchDepotListSuccess,
	props<{ depotList: DepotListEntity }>()
);

export const fetchDepotListFail = createAction(
	DepotActions.FetchDepotListFail,
	props<{ error: unknown }>()
);

export const updateEntitiesCountForDepot = createAction(
	DepotActions.UpdateEntitiesCountForDepot,
	props<{ ids: Array<number>; shouldReload: boolean }>()
);

export const updateEntitiesCountForDepotSuccess = createAction(
	DepotActions.UpdateEntitiesCountForDepotSuccess,
	props<{ depotList: DepotListEntity }>()
);

export const updateEntitiesCountForDepotFailure = createAction(
	DepotActions.UpdateEntitiesCountForDepotFailure,
	props<{ error: unknown }>()
);

export const navigateToDepot = createAction(
	DepotActions.NavigateToDepotRoute,
	props<{
		id: number;
		depotType: string;
	}>()
);
export const navigateToDepotCreate = createAction(
	DepotActions.NavigateToDepotCreateRoute
);
export const navigateToDepotDuplicate = createAction(
	DepotActions.NavigateToDepotDuplicateRoute,
	props<{ id: number; depotType: string }>()
);

export const updateIncludePublic = createAction(
	DepotActions.UpdateIncludePublic,
	props<{ includePublic: boolean }>()
);

export const openDeleteConfirmationPopup = createAction(
	DepotActions.OpenDeleteConfirmationPopup,
	props<{ depotId: Array<number>; depotLabel: string }>()
);
export const deleteDepot = createAction(
	DepotActions.DeleteDepot,
	props<{ depotId: number }>()
);

export const bulkDeleteDepots = createAction(
	DepotActions.BulkDeleteDepots,
	props<{ depotIds: Array<number> }>()
);

export const deleteDepotFail = createAction(DepotActions.DeleteDepotFail);

export const toggleDepotStatus = createAction(
	DepotActions.ToggleDepotStatus,
	props<{ depotId: number; depotType: string; status: boolean }>()
);

export const openCreateDepotEntityModal = createAction(
	DepotActions.OpenCreateDepotEntityModal,
	props<{ depotId: number; depotLabel: string; depotType: string }>()
);

export const resetAvailableHeaders = createAction(
	DepotActions.ResetAvailableHeaders
);

export const initAvailableDepotHeadersForEntityCreation = createAction(
	DepotActions.InitAvailableDepotHeadersForEntityCreation,
	props<{ depotId: number; depotType: string }>()
);

export const fetchDepotHeadersSuccess = createAction(
	DepotActions.FetchDepotHeadersSuccess,
	props<{
		headers: DepotHeaderListEntity;
		availableTags: OrderedSet<AppTagItemEntity>;
		availableCharacteristicTypes: Map<number, CharacteristicType>;
		availableDateFormats: Map<string, DcTimestampFormat>;
		availableTimezones: Array<string>;
		supportedLanguages: Map<string, SupportedLanguageEntity>;
	}>()
);

export const fetchDepotHeadersFail = createAction(
	DepotActions.FetchDepotHeadersFail,
	props<{ error: unknown }>()
);

export const createEntityFromDepot = createAction(
	DepotActions.CreateEntityFromDepot,
	props<{
		depotId: number;
		metadata: AppMetadataEntity;
		headers: Array<DepotHeaderItemEntity>;
	}>()
);

export const createEntityFromDepotSuccess = createAction(
	DepotActions.CreateEntityFromDepotSuccess
);

export const createEntityFromDepotFail = createAction(
	DepotActions.CreateEntityFromDepotFail,
	props<{ error: unknown }>()
);

export const openDepotHeaderFieldsSelection = createAction(
	DepotActions.OpenDepotHeaderFieldsSelection,
	props<{ params: DepotSchemaConfig; currentSchema: unknown | null }>()
);

export const replaceCurrentLabelWith = createAction(
	DepotActions.ReplaceCurrentLabel,
	props<{ searchTerm: string; replaceWith: string }>()
);

export const triggerDetectAvailableHeaders = createAction(
	DepotActions.TriggerDetectAvailableHeaders,
	props<{ params: DepotSchemaConfig }>()
);

export const reconstructDepotSchemaMappingSuccess = createAction(
	DepotActions.ReconstructDepotSchemaMappingSuccess,
	props<{ mapping: Array<DepotSchemaEntity> }>()
);

export const reconstructDepotSchemaMappingError = createAction(
	DepotActions.ReconstructDepotSchemaMappingError,
	props<{ error: unknown }>()
);

export const fetchDepotSchemaMappingSuccess = createAction(
	DepotActions.FetchDepotSchemaMappingSuccess,
	props<{ mapping: Array<DepotSchemaEntity> }>()
);

export const fetchDepotSchemaMappingError = createAction(
	DepotActions.FetchDepotSchemaMappingError,
	props<{ error: unknown }>()
);

export const initDepotHeaderSetting = createAction(
	DepotActions.InitDepotHeaderSetting
);

export const getDepotHeaderSettingSuccess = createAction(
	DepotActions.GetDepotHeaderSettingSuccess,
	props<{
		availableCharacteristicTypes: Map<number, CharacteristicType>;
		availableDateFormats: Map<string, DcTimestampFormat>;
		availableTimezones: Array<string>;
		supportedLanguages: Map<string, SupportedLanguageEntity>;
	}>()
);

export const getDepotHeaderSettingFail = createAction(
	DepotActions.GetDepotHeaderSettingFail,
	props<{ error: unknown }>()
);
